import { AlpineData, NOTIFICATION_INFINITE_DURATION, NotificationAlert, NotificationAlertType, isValidNotificationAlertType } from "@/js/interfaces/Components/alpinejs/NotificationAlert";
import { listen, sanitizeHtmlTag } from '@/js/app/support/utils';
import is from "../../support/is";
import { createAlpineComponent } from "@nolikein/typed-alpinejs";

/* Generated from the "artisan make:alpine" command */
export default {
    name: 'NotificationAlert',
    component: (
        // Component parameters...
    ) => createAlpineComponent<AlpineData>({
        // Component properties ...
        list: new Map(),
        idCounter: 1,

        init() {
            listen('notify-alert', (e: CustomEvent) => {
                const data = <object>e.detail;

                // Check the title
                if (!('title' in data)) {
                    throw Error('Cannot send a notification alert without title');
                }
                if (!is.string(data.title)) {
                    throw Error('The alert notification title should be a string');
                }

                // Check the content
                if (!('message' in data)) {
                    throw Error('Cannot send a notification alert without message');
                }
                if (!is.string(data.message)) {
                    throw Error('The alert notification message should be a string');
                }

                // Check the type
                if (!('type' in data)) {
                    throw Error('Cannot send a notification alert without alert type');
                }
                if (!is.string(data.type)) {
                    throw Error('The alert notification type should be a string');
                }
                if (!isValidNotificationAlertType(data.type)) {
                    throw Error(`The alert notification type is invalid. Given ${data.type}`);
                }

                // Check the duration
                if (!('duration' in data)) {
                    throw Error('Cannot send a notification alert without a duration');
                }
                if (!is.number(data.duration)) {
                    throw Error('The alert notification type should be a number');
                }
                if (data.duration < NOTIFICATION_INFINITE_DURATION) {
                    throw Error('The duration cannot be lower than -1 which represent infinity.');
                }

                // Check onClose
                let onClose = null;
                if ('onClose' in data) {
                    if (null !== data.onClose && !(is.function(data.onClose))) {
                        throw Error('The banner notification onClose should be a function');
                    }
                    onClose = data.onClose;
                }

                this.addAlert(data.title, data.message, data.type, data.duration, onClose);
            });
        },

        /**
         * Append an alert
         * @param duration -1 Equals to infinite
         */
        addAlert(title: string, message: string, type: NotificationAlertType, duration: number = -1, onClose: NotificationAlert["onClose"] = null): void {
            this.list.set(this.idCounter++, {
                title,
                message,
                type,
                duration,
                onClose,
            });
        },

        getAlerts(): Array<[number, NotificationAlert]> {
            return Array.from(this.list.entries());
        },

        closeAlert(id: number): void {
            if (this.list.has(id)) {
                const item = this.list.get(id) as NotificationAlert;
                if (null !== item.onClose) {
                    item.onClose();
                }

                this.list.delete(id);
            }
        },

        onAppearing(id: number): void {
            const alert = this.list.get(id);

            if (undefined === alert) {
                throw Error('Cannot check animation for non existing alert');
            }

            if (alert.duration > NOTIFICATION_INFINITE_DURATION) {
                // Delete the alert after the duration time
                setTimeout(() => {
                    this.closeAlert(id);
                }, alert.duration);

                // Update the duration of the animation
                const footer = this.$el.children[1] as HTMLElement;
                footer.style.animationDuration = alert.duration + 'ms';
            }
        },

        getAlertMessage(alert: NotificationAlert): string {
            // Clean message to prevent XSS. Use the sanitizeHtmlTag function in utils
            return sanitizeHtmlTag(alert.message, { allow: ['a'] });
        },
    }),
};
